// Il cuore della pagina ordine (redesign 2026-10-23, Davide: «modifiche più
// profonde, soprattutto per la funzionalità operativa… voglio poter fare
// TUTTO»). Due pezzi, una fonte sola (``percorso.ts``):
//
//  1. «Prossimo passo»: una frase grande con il tasto che la fa. Si legge per
//     prima e basta da sola — chi apre l'ordine sa cosa fare senza cercarlo.
//  2. Il percorso: cinque tappe in fila (fornitore → etichetta → ritiro →
//     spedito → fattura), ognuna con lo stato in parole e i SUOI tasti. Prima
//     i tasti stavano in una griglia unica sotto la spedizione, in testata e
//     in lista: qui ogni tasto sta accanto al passo che fa avanzare.
//
// La tappa «adesso» è una sola: la prima ancora da fare. Il suo tasto
// principale compare anche nella fascia sopra — stesse parole, stesso gesto.
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { FlussoFornitore } from "../../useOrdinaFornitore";
import { STATO_SPEDITO } from "../../confermaBulk";
import {
  type AzionePasso,
  type ContestoPercorso,
  type ProssimoPasso,
  type StatoTappa,
  type Tappa,
  type TappaId,
  prossimoPasso,
  tappeOrdine,
} from "../../percorso";
import type { Order } from "../../types";
import { ConfermaBonificoButton } from "../BonificoButton";
import { MagazzinoRilasciaButton } from "../MagazzinoRilasciaButton";
import { CopyButton } from "../OrderCard";
import { StampaZebraButton } from "../StampaZebraButton";
import { SupplierOrderRefEditor } from "../SupplierOrderRefEditor";
import {
  AllServiceLdvButton,
  BTN_TAPPA,
  BorderoButton,
  CambiaStato,
  ContestazioneButton,
  ScaricaLdvButton,
  SegnaPagatoButton,
  haLdv,
} from "./azioni";
import { InviaLdvButton } from "./InviaLdvButton";
import { riassuntoColli } from "./ShippingSection";
import { BTN, BTN_PRIMARY } from "./stile";
import { nomeCorriere } from "../../nomeCorriere";

export interface PercorsoOrdineProps {
  order: Order;
  ctx: ContestoPercorso;
  /** I flussi «Ordina a …» di questo ordine, col loro ``run``. */
  flussi: FlussoFornitore[];
  ordinaBusy: boolean;
  onGeneraLdv: (redo: boolean) => void;
  labelBusy: boolean;
  onInviaLdv: (order: Order) => Promise<void>;
  pickupMailBusy: boolean;
  onConfermaCanale: () => void;
  confermaBusy: boolean;
  onStato: (stato: string) => void;
  statoBusy: boolean;
  onChanged: () => void;
  onMessage: (msg: string) => void;
}

/** I tasti condivisi con la lista (ActionButton inline) a tutta larghezza. */
const LARGO = "[&>span]:flex [&>span]:w-full [&_button]:w-full [&_button]:justify-center";

const BTN_HERO = `${BTN_PRIMARY} min-h-[46px] px-5 text-[15px]`;
const BTN_HERO_SEC = `${BTN} min-h-[46px] px-5 text-[15px]`;

/** Colore della fascia: lo decide l'urgenza, mai il testo (che resta nero). */
const TONO: Record<ProssimoPasso["tono"], { barra: string; fondo: string }> = {
  "da-fare": {
    barra: "bg-emerald-700",
    fondo: "bg-[radial-gradient(120%_160%_at_100%_0%,rgb(209_250_229/0.7),transparent_60%)]",
  },
  attesa: {
    barra: "bg-amber",
    fondo: "bg-[radial-gradient(120%_160%_at_100%_0%,rgb(var(--om-amber-soft)),transparent_60%)]",
  },
  fatto: {
    barra: "bg-teal",
    fondo: "bg-[radial-gradient(120%_160%_at_100%_0%,rgb(var(--om-teal-soft)),transparent_60%)]",
  },
  fermo: {
    barra: "bg-red",
    fondo: "bg-[radial-gradient(120%_160%_at_100%_0%,rgb(var(--om-red-soft)),transparent_60%)]",
  },
};

const STATO_PAROLA: Record<StatoTappa, string> = {
  fatto: "Fatto",
  ora: "Adesso",
  dopo: "Dopo",
  salta: "Non serve",
};

const STATO_CHIP: Record<StatoTappa, string> = {
  fatto: "bg-emerald-50 text-emerald-800",
  ora: "bg-emerald-700 text-white",
  dopo: "bg-paper text-ink",
  salta: "bg-paper text-ink",
};

function Segno({ stato }: { stato: StatoTappa }) {
  if (stato === "fatto") {
    return (
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-700 text-white">
        <svg
          viewBox="0 0 24 24"
          className="h-3.5 w-3.5 fill-none stroke-current stroke-[3]"
          aria-hidden="true"
        >
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
      </span>
    );
  }
  if (stato === "ora") {
    return (
      <span className="om-pulse grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 border-emerald-700 bg-card">
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-700" />
      </span>
    );
  }
  return (
    <span
      className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 bg-card ${
        stato === "salta" ? "border-dashed border-line-2" : "border-line-2"
      }`}
    />
  );
}

function giorno(iso: string): string {
  const [, mm, gg] = iso.slice(0, 10).split("-");
  return gg && mm ? `${gg}/${mm}` : iso;
}

export function PercorsoOrdine(props: PercorsoOrdineProps) {
  const { order, ctx } = props;
  const tappe = tappeOrdine(order, ctx);
  const passo = prossimoPasso(order, ctx);
  const tono = TONO[passo.tono];
  const fatte = tappe.filter((t) => t.stato === "fatto" || t.stato === "salta").length;

  return (
    <div className="flex flex-col gap-3">
      <section
        data-testid="prossimo-passo"
        aria-labelledby="prossimo-passo-titolo"
        className={`om-rise relative overflow-hidden rounded-2xl border border-line bg-card ${tono.fondo}`}
      >
        <span className={`absolute inset-y-0 left-0 w-1.5 ${tono.barra}`} aria-hidden="true" />
        <div className="grid gap-4 py-5 pl-7 pr-6 md:grid-cols-[minmax(0,1fr)_auto] md:items-center max-md:pl-5 max-md:pr-4">
          <div className="min-w-0 max-w-5xl">
            <p className="text-[13px] font-semibold text-ink">Prossimo passo</p>
            <h2
              id="prossimo-passo-titolo"
              className="mt-0.5 font-display text-[clamp(1.55rem,2.3vw,2.2rem)] font-semibold leading-[1.1] tracking-[-0.02em] text-ink"
            >
              {passo.titolo}
            </h2>
            <p className="mt-1.5 text-[15px] leading-relaxed text-ink">{passo.perche}</p>
          </div>
          {passo.azione && (
            <div className="flex flex-wrap gap-2">
              <TastoPasso azione={passo.azione} tono={passo.tono} {...props} />
            </div>
          )}
        </div>
      </section>

      {/* Quanto manca: una barra sola sopra le tappe. Conta anche le tappe
          che non servono — per chi legge sono strada fatta. */}
      <div
        className="h-1 overflow-hidden rounded-full bg-line"
        role="progressbar"
        aria-label="Avanzamento dell'ordine"
        aria-valuemin={0}
        aria-valuemax={tappe.length}
        aria-valuenow={fatte}
      >
        <div
          className="om-fill h-full rounded-full bg-emerald-700"
          style={{ width: `${(fatte / tappe.length) * 100}%` }}
        />
      </div>

      {/* Griglia senza buchi: 5 tappe in 5 colonne, i filetti sono lo sfondo
          che passa nel gap da 1px. Sotto lg una tappa per riga. */}
      <ol
        data-testid="percorso"
        aria-label="Percorso dell'ordine"
        className="grid gap-px overflow-hidden rounded-2xl border border-line bg-line lg:grid-cols-5"
      >
        {tappe.map((t, i) => (
          <li
            key={t.id}
            data-testid={`tappa-${t.id}`}
            aria-current={t.stato === "ora" ? "step" : undefined}
            className={`om-rise flex min-w-0 flex-col p-4 ${
              t.stato === "ora" ? "bg-emerald-50/70" : "bg-card"
            }`}
            style={{ animationDelay: `${60 + i * 40}ms` }}
          >
            <div className="flex items-center gap-2.5">
              <Segno stato={t.stato} />
              <h3 className="font-display text-[17px] font-semibold leading-tight text-ink">
                {t.titolo}
              </h3>
              <span
                className={`ml-auto rounded-full px-2 py-0.5 text-[12px] font-semibold ${STATO_CHIP[t.stato]}`}
              >
                {STATO_PAROLA[t.stato]}
              </span>
            </div>
            <p className="mt-2 text-[14px] leading-snug text-ink">{t.riga}</p>
            <Dettaglio tappa={t} {...props} />
            <div className="mt-3 flex flex-col gap-1.5">
              <AzioniTappa tappa={t} {...props} />
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Il tasto della fascia «Prossimo passo»: lo stesso gesto della tappa. */
function TastoPasso({
  azione,
  tono,
  order,
  ctx,
  flussi,
  ordinaBusy,
  onGeneraLdv,
  labelBusy,
  onInviaLdv,
  pickupMailBusy,
  onConfermaCanale,
  confermaBusy,
  onStato,
  statoBusy,
  onChanged,
}: PercorsoOrdineProps & { azione: AzionePasso; tono: string }) {
  switch (azione.tipo) {
    case "ordina": {
      const f = flussi.find((x) => x.id === azione.flusso);
      if (!f) return null;
      return (
        <button type="button" disabled={ordinaBusy} onClick={f.run} className={BTN_HERO}>
          {ordinaBusy ? "Preparo…" : `Ordina a ${f.label}`}
        </button>
      );
    }
    case "ldv":
      return (
        <button
          type="button"
          disabled={labelBusy}
          onClick={() => onGeneraLdv(false)}
          className={BTN_HERO}
        >
          {labelBusy ? "Genero…" : "Genera LDV"}
        </button>
      );
    case "invia-ldv":
      return (
        <button
          type="button"
          disabled={pickupMailBusy}
          onClick={() => void onInviaLdv(order)}
          className={BTN_HERO}
        >
          {pickupMailBusy ? "Preparo…" : "Manda la LDV"}
        </button>
      );
    case "conferma-canale":
      return (
        <button
          type="button"
          disabled={confermaBusy}
          onClick={onConfermaCanale}
          className={BTN_HERO}
        >
          {confermaBusy ? "Avviso…" : `Conferma a ${ctx.canaleDiretto ?? "canale"}`}
        </button>
      );
    case "spedito":
      return (
        <button
          type="button"
          disabled={statoBusy}
          onClick={() => onStato(STATO_SPEDITO)}
          className={BTN_HERO}
        >
          Segna spedito
        </button>
      );
    case "bonifico":
      return <ConfermaBonificoButton order={order} onDone={onChanged} />;
    case "ritiri":
      return (
        <Link to="/ritiri" className={tono === "da-fare" ? BTN_HERO : BTN_HERO_SEC}>
          Apri i ritiri
        </Link>
      );
    case "fattura":
      return (
        <Link
          to={`/fatturazione?q=${encodeURIComponent(order.id)}`}
          className={tono === "da-fare" ? BTN_HERO : BTN_HERO_SEC}
        >
          Apri in Fatturazione
        </Link>
      );
    case "ldv-allservice":
      return null;
  }
}

/** Il dato che serve per decidere, sotto la riga della tappa. */
function Dettaglio({ tappa, order, ctx, onChanged }: PercorsoOrdineProps & { tappa: Tappa }) {
  const supplierRef = order.supplierOrderRef ?? "";
  switch (tappa.id) {
    case "fornitore":
      // Numero ordine fornitore: si legge, si copia, si corregge QUI — e' il
      // dato di questa tappa. Stava in testata, lontano dai tasti che lo usano.
      return (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {supplierRef && (
            <>
              <span className="font-mono text-[13.5px] font-semibold tracking-[-0.02em] text-ink">
                {supplierRef}
              </span>
              <CopyButton text={supplierRef} label="Copia numero ordine fornitore" />
            </>
          )}
          <SupplierOrderRefEditor order={order} onSaved={onChanged} />
        </div>
      );
    case "etichetta":
      return (
        <p className="mt-1 text-[13px] text-ink">
          {nomeCorriere(order.corriere)} · {riassuntoColli(order)}
        </p>
      );
    case "ritiro":
      return ctx.ritiro ? (
        <p className="mt-1 text-[13px] text-ink" data-testid="ritiro-prenotato">
          Ritiro OM del {giorno(ctx.ritiro.data)}
          {ctx.ritiro.codice ? ` · ${ctx.ritiro.codice}` : ""}
        </p>
      ) : null;
    default:
      return null;
  }
}

function TastoTappa({
  children,
  primario,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { primario?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      {...rest}
      className={primario ? `${BTN_PRIMARY} w-full text-center leading-tight` : BTN_TAPPA}
    >
      {children}
    </button>
  );
}

/** I tasti di una tappa. D1 (Davide 2026-07-16): un tasto che qui non si
 *  applica NON si renderizza — niente tasti grigi con il motivo nel tooltip. */
function AzioniTappa(props: PercorsoOrdineProps & { tappa: Tappa }) {
  const {
    tappa,
    order,
    ctx,
    flussi,
    ordinaBusy,
    onGeneraLdv,
    labelBusy,
    onInviaLdv,
    pickupMailBusy,
    onConfermaCanale,
    confermaBusy,
    onStato,
    statoBusy,
    onChanged,
    onMessage,
  } = props;
  const ora = tappa.stato === "ora";
  const ldv = haLdv(order);
  const id: TappaId = tappa.id;

  if (id === "fornitore") {
    const ordinaBottoni = flussi.map((f, i) => (
      <TastoTappa
        key={f.id}
        primario={ora && i === 0}
        disabled={ordinaBusy}
        title={f.desc}
        onClick={f.run}
      >
        {`Ordina a ${f.label}`}
      </TastoTappa>
    ));
    return (
      <>
        {tappa.stato === "fatto" && flussi.length > 0 ? (
          // Gia' ordinato: rimandarlo e' raro e costa un acquisto doppio —
          // c'e', ma va aperto apposta.
          <details className="group rounded-md border border-line-2">
            <summary className="flex min-h-[38px] cursor-pointer list-none items-center justify-between px-3 text-[13px] font-semibold text-ink">
              Ordina di nuovo
              <span aria-hidden="true" className="transition-transform group-open:rotate-180">
                ▾
              </span>
            </summary>
            <div className="flex flex-col gap-1.5 border-t border-line p-2">{ordinaBottoni}</div>
          </details>
        ) : (
          ordinaBottoni
        )}
        <SegnaPagatoButton order={order} onChanged={onChanged} />
        {/* Ultimo: il suo esito pende sotto il tasto, e sotto non c'e' altro. */}
        <div className={LARGO}>
          <MagazzinoRilasciaButton order={order} onDone={onChanged} variant="inline" />
        </div>
      </>
    );
  }

  if (id === "etichetta") {
    if (ctx.isAllService) {
      return (
        <>
          <AllServiceLdvButton order={order} onMessage={onMessage} onChanged={onChanged} />
          <ScaricaLdvButton order={order} />
        </>
      );
    }
    return (
      <>
        <TastoTappa
          primario={!ldv}
          disabled={labelBusy}
          title={
            labelBusy ? "Genero l'etichetta…" : "Genera la LDV con le misure dei colli qui sotto"
          }
          onClick={() => onGeneraLdv(false)}
        >
          {labelBusy ? "Genero…" : "Genera LDV"}
        </TastoTappa>
        {ldv && (
          <TastoTappa
            data-testid="redo-label-btn"
            disabled={labelBusy}
            title="Annulla la spedizione e ne crea una nuova con i dati attuali (mittente, corriere, colli). Su un ordine già spedito chiede conferma."
            onClick={() => onGeneraLdv(true)}
          >
            {labelBusy ? "Rifaccio…" : "Rifai LDV"}
          </TastoTappa>
        )}
        <ScaricaLdvButton order={order} />
        <BorderoButton order={order} />
        {/* Ultimo: l'esito della stampa pende sotto il tasto. Il colore resta
            quello di ActionButton — dice a che punto e' il lavoro di stampa. */}
        {ldv && (
          <div className={LARGO}>
            <StampaZebraButton order={order} variant="inline" />
          </div>
        )}
      </>
    );
  }

  if (id === "ritiro") {
    return (
      <>
        {ctx.pickupMailReady && (
          <InviaLdvButton order={order} busy={pickupMailBusy} onSend={onInviaLdv} />
        )}
        {!ctx.isAllService && (
          <Link to="/ritiri" className={BTN_TAPPA}>
            Apri i ritiri
          </Link>
        )}
      </>
    );
  }

  if (id === "spedito") {
    const canale = ctx.canaleDiretto && order.trackingCode ? ctx.canaleDiretto : null;
    return (
      <>
        {canale && (
          <TastoTappa
            data-testid="conferma-canale-btn"
            primario={ora}
            disabled={confermaBusy}
            title={`Dice a ${canale} che il pacco è partito e gli passa il tracking, senza passare da Poleepo. Il cliente riceve la mail "spedito".`}
            onClick={onConfermaCanale}
          >
            {confermaBusy ? "Avviso…" : `Conferma a ${canale}`}
          </TastoTappa>
        )}
        {!canale && order.state !== "spedito" && ldv && (
          <TastoTappa primario={ora} disabled={statoBusy} onClick={() => onStato(STATO_SPEDITO)}>
            Segna spedito
          </TastoTappa>
        )}
        {ldv && <ContestazioneButton order={order} />}
        <CambiaStato onApplica={onStato} busy={statoBusy} />
      </>
    );
  }

  // fattura
  return (
    <>
      {ctx.bonificoDaVerificare && <ConfermaBonificoButton order={order} onDone={onChanged} />}
      <Link to={`/fatturazione?q=${encodeURIComponent(order.id)}`} className={BTN_TAPPA}>
        Apri in Fatturazione
      </Link>
    </>
  );
}
